import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {

    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Checkbox,
    FormControlLabel,
    Box,
    Grid,
    FormHelperText,
    Divider
} from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    formContainer: {
        padding: theme.spacing(3),
        maxWidth: 1200,
        margin: '0 auto',
    },
    formTitle: {
        marginBottom: theme.spacing(3),
        fontWeight: 'bold',
        color: theme.palette.text.primary,
    },
    formRow: {
        marginBottom: theme.spacing(2),
    },
    addNewButton: {
        minWidth: 'auto',
        padding: theme.spacing(1, 2),
        textTransform: 'none',
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
        },
    },
    submitButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(3),
    },
    submitButton: {

        color: 'white',
        padding: theme.spacing(1.5, 3),
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        width: '250px', // Fixed width for the button
        '&:hover': {

        },
    },
    addNewSection: {
        backgroundColor: theme.palette.grey[50],
        border: `2px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2.5),
        marginTop: theme.spacing(2.5),
    },
    sectionTitle: {
        marginBottom: theme.spacing(2.5),
        fontWeight: 'bold',
        color: theme.palette.text.primary,
    },
    checkboxGroup: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
    },
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'flex-end',
        marginTop: theme.spacing(1),
    },
    saveButton: {

        color: 'white',
        padding: theme.spacing(1.25, 2.5),
        fontWeight: 'bold',

    },
    cancelButton: {

        color: 'white',
        padding: theme.spacing(1.25, 2.5),
        fontWeight: 'bold',

    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
    },
    errorField: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: theme.palette.error.main,
            },
            backgroundColor: '#fff5f5',
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.error.main,
        },
    },
}));

// Validation helper functions
const validateRoutingNumber = (routingNumber) => {
    return /^\d{9}$/.test(routingNumber);
};

const validateAccountNumber = (accountNumber) => {
    return /^\d{8,17}$/.test(accountNumber);
};

const ACHPaymentForm = ({ isSubmitting, onSubmit, onCancel, paymentMethods = [], allowPaymentDateSelection = false }) => {
    const classes = useStyles();
    const [paymentMethod, setPaymentMethod] = useState('');
    // Set default payment date to today if not allowed to select
    const todayStr = new Date().toISOString().split('T')[0];
    const [paymentDate, setPaymentDate] = useState(allowPaymentDateSelection ? '' : todayStr);
    const [referenceNumber, setReferenceNumber] = useState('');
    const [showAddNew, setShowAddNew] = useState(false);
    const [saveForFutureUse, setSaveForFutureUse] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [newPaymentMethod, setNewPaymentMethod] = useState(null);
    


    // ACH payment form fields
    const [achDetails, setAchDetails] = useState({
        routingNumber: '',
        accountNumber: '',
        bankName: '',
        city: '',
        state: '',
        nameOnAccount: '',
        accountNickname: ''
    });

    // Set default payment method when paymentMethods are available
    React.useEffect(() => {
        if (paymentMethods.length > 0 && !paymentMethod) {
            const activePaymentMethod = paymentMethods.find(pm => pm.isActive);
            if (activePaymentMethod) {
                setPaymentMethod(activePaymentMethod.bankAccountId.toString());
            } else {
                setPaymentMethod(paymentMethods[0].bankAccountId.toString());
            }
        }
    }, [paymentMethods, paymentMethod]);

    // If not allowed to select payment date, always set to today
    React.useEffect(() => {
        if (!allowPaymentDateSelection) {
            setPaymentDate(todayStr);
        }
    }, [allowPaymentDateSelection]);

    // Filter active payment methods and include new payment method if it exists
    const activePaymentMethods = paymentMethods.filter(pm => pm.isActive);
    const allAvailablePaymentMethods = newPaymentMethod
        ? [...activePaymentMethods, newPaymentMethod]
        : activePaymentMethods;

    // US States list
    const usStates = [
        { value: '', label: 'Select State' },
        { value: 'AL', label: 'Alabama' },
        { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' },
        { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' },
        { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' },
        { value: 'DE', label: 'Delaware' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' },
        { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' },
        { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' },
        { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' },
        { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' },
        { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' },
        { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' },
        { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' },
        { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' },
        { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' },
        { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' },
        { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' },
        { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' },
        { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' },
        { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' },
        { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' },
        { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' },
        { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' },
        { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' },
        { value: 'WY', label: 'Wyoming' }
    ];

    const handleAddNewClick = () => {
        setShowAddNew(true);
    };

    const handleSaveNewPayment = () => {
        // Validate only ACH details for saving
        const errors = {};

        // Routing Number validation - exactly 9 digits
        if (!achDetails.routingNumber) {
            errors.routingNumber = 'Routing number is required';
        } else if (!validateRoutingNumber(achDetails.routingNumber)) {
            errors.routingNumber = 'Routing number must be exactly 9 digits';
        }

        // Account Number validation - numeric and between 8-17 digits
        if (!achDetails.accountNumber) {
            errors.accountNumber = 'Account number is required';
        } else if (!validateAccountNumber(achDetails.accountNumber)) {
            errors.accountNumber = 'Account number must be numeric and between 8-17 digits';
        }

        // Bank Name validation - not empty
        if (!achDetails.bankName.trim()) {
            errors.bankName = 'Bank name is required';
        }

        // Name on Account validation - not empty
        if (!achDetails.nameOnAccount.trim()) {
            errors.nameOnAccount = 'Name on account is required';
        }

        // City validation - not empty
        if (!achDetails.city.trim()) {
            errors.city = 'City is required';
        }

        // State validation - must be selected
        if (!achDetails.state) {
            errors.state = 'State is required';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Clear validation errors and close the add new section
        setValidationErrors({});
        setShowAddNew(false);

        // Create a new payment method object with a temporary ID
        const newPaymentMethodObj = {
            accountName: achDetails.accountNickname || `${achDetails.bankName} - ${achDetails.accountNumber.slice(-4)}`,
            bankAccountId: 'GEN_ID', // Temporary ID using timestamp
            isActive: true,
            isNewAccount: true, // Flag to identify new accounts
            achDetails: { ...achDetails }
        };

        // Store the new payment method
        setNewPaymentMethod(newPaymentMethodObj);

        // Update the payment method selection to the new account
        setPaymentMethod(newPaymentMethodObj.bankAccountId.toString());

        // Here you could call an API to save the payment method
        // For now, we'll just close the section
        console.log('Payment method saved:', achDetails);
        console.log('New payment method created:', newPaymentMethodObj);


    };

    const handleCancelNewPayment = () => {
        setShowAddNew(false);
        setNewPaymentMethod(null);
        setAchDetails({
            routingNumber: '',
            accountNumber: '',
            bankName: '',
            city: '',
            state: '',
            nameOnAccount: '',
            accountNickname: ''
        });
        setSaveForFutureUse(false);

        // Clear any validation errors related to ACH details
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.routingNumber;
            delete newErrors.accountNumber;
            delete newErrors.bankName;
            delete newErrors.city;
            delete newErrors.state;
            delete newErrors.nameOnAccount;
            return newErrors;
        });
    };

    const handleAchDetailChange = (field, value) => {
        setAchDetails(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};

        // Validate payment method selection
        if (!paymentMethod) {
            errors.paymentMethod = 'Please select a payment method';
        }

        // Validate payment date (required)
        if (!paymentDate) {
            errors.paymentDate = 'Payment date is required';
        } else if (allowPaymentDateSelection) {
            const today = new Date().toISOString().split('T')[0];
            if (paymentDate < today) {
                errors.paymentDate = 'Payment date cannot be in the past';
            }
        }

        // Validate reference number
        if (!referenceNumber.trim()) {
            errors.referenceNumber = 'Reference number is required';
        }

        // Validate ACH details if adding new payment method
        if (showAddNew) {
            // Routing Number validation - exactly 9 digits
            if (!achDetails.routingNumber) {
                errors.routingNumber = 'Routing number is required';
            } else if (!validateRoutingNumber(achDetails.routingNumber)) {
                errors.routingNumber = 'Routing number must be exactly 9 digits';
            }

            // Account Number validation - numeric and between 8-17 digits
            if (!achDetails.accountNumber) {
                errors.accountNumber = 'Account number is required';
            } else if (!validateAccountNumber(achDetails.accountNumber)) {
                errors.accountNumber = 'Account number must be numeric and between 8-17 digits';
            }

            // Bank Name validation - not empty
            if (!achDetails.bankName.trim()) {
                errors.bankName = 'Bank name is required';
            }

            // Name on Account validation - not empty
            if (!achDetails.nameOnAccount.trim()) {
                errors.nameOnAccount = 'Name on account is required';
            }

            // City validation - not empty
            if (!achDetails.city.trim()) {
                errors.city = 'City is required';
            }

            // State validation - must be selected
            if (!achDetails.state) {
                errors.state = 'State is required';
            }
        }

        return errors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const errors = validateForm();

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Clear validation errors on successful validation
        setValidationErrors({});
        
        // Find the selected payment method details
        let selectedPaymentMethodDetails = paymentMethods.find(pm => pm.bankAccountId.toString() === paymentMethod);

        // If not found in existing payment methods, check if it's the new payment method
        if (!selectedPaymentMethodDetails && newPaymentMethod && newPaymentMethod.bankAccountId.toString() === paymentMethod) {
            selectedPaymentMethodDetails = newPaymentMethod;
        }

        const paymentData = {
            paymentMethod,
            paymentDate,
            referenceNumber,
            achDetails: showAddNew ? achDetails : (newPaymentMethod?.achDetails || null),
            saveForFutureUse,
            selectedPaymentMethodDetails,
            isNewPaymentMethod: newPaymentMethod ? true : false
        };

        if (onSubmit) {
            onSubmit(paymentData);
        } else {
            onCancel();
        }

    };

    return (
        <Paper className={classes.formContainer}>
            <Typography variant="h4" color="textPrimary" style={{ marginBottom: '16px' }}>
                Make a Payment
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3} className={classes.formRow}>
                    <Grid item xs={12} md={4}>
                        <FormControl
                            variant="outlined"
                            fullWidth
                            error={!!validationErrors.paymentMethod}
                        >
                            <InputLabel>ACH Payment Method</InputLabel>
                            <Select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                label="ACH Payment Method"
                            >
                                <MenuItem value="">Select Payment Method</MenuItem>
                                {allAvailablePaymentMethods.map((pm) => (
                                    <MenuItem key={pm.bankAccountId} value={pm.bankAccountId.toString()}>
                                        {pm.accountName} {pm.isNewAccount ? '(New)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                            {validationErrors.paymentMethod && (
                                <FormHelperText>{validationErrors.paymentMethod}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Payment Date"
                            type="date"
                            value={paymentDate}
                            onChange={allowPaymentDateSelection ? (e) => setPaymentDate(e.target.value) : undefined}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={!!validationErrors.paymentDate}
                            helperText={validationErrors.paymentDate}
                            disabled={!allowPaymentDateSelection}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Reference Number"
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            variant="outlined"
                            fullWidth
                            error={!!validationErrors.referenceNumber}
                            helperText={validationErrors.referenceNumber}
                        />
                    </Grid>

                </Grid>

                <Grid container spacing={3} className={classes.formRow}>
                    <Grid item xs={12}>
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={handleAddNewClick}
                            className={classes.addNewButton}
                        >
                            Add New
                        </Button>
                    </Grid>
                </Grid>
                {/* Submit button in its own container, aligned right */}
                <div className={classes.submitButtonContainer}>
                    <Button
                        type="submit"
                        variant="contained"
                        className={classes.submitButton}
                        color='primary'
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'SUBMIT PAYMENT'}
                    </Button>
                </div>
                {showAddNew && (
                    <Paper className={classes.addNewSection}>
                        <Typography variant="h4" color="textPrimary" style={{ marginBottom: '16px' }}>
                            Adding New Payment Method
                        </Typography>

                        <Grid container spacing={2} className={classes.formRow}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Routing Number"
                                    value={achDetails.routingNumber}
                                    onChange={(e) => handleAchDetailChange('routingNumber', e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    style={{ backgroundColor: '#fff' }}
                                    placeholder="123456789"
                                    inputProps={{ maxLength: 9 }}
                                    error={!!validationErrors.routingNumber}
                                    helperText={validationErrors.routingNumber}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Account Number"
                                    value={achDetails.accountNumber}
                                    onChange={(e) => handleAchDetailChange('accountNumber', e.target.value)}
                                    variant="outlined"
                                    style={{ backgroundColor: '#fff' }}
                                    fullWidth
                                    placeholder="98765432198765"
                                    inputProps={{ maxLength: 17 }}
                                    error={!!validationErrors.accountNumber}
                                    helperText={validationErrors.accountNumber}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Bank Name"
                                    value={achDetails.bankName}
                                    onChange={(e) => handleAchDetailChange('bankName', e.target.value)}
                                    variant="outlined"
                                    style={{ backgroundColor: '#fff' }}
                                    fullWidth
                                    placeholder="Bank of America"
                                    error={!!validationErrors.bankName}
                                    helperText={validationErrors.bankName}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} className={classes.formRow}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="City"
                                    value={achDetails.city}
                                    style={{ backgroundColor: '#fff' }}
                                    onChange={(e) => handleAchDetailChange('city', e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    placeholder="Boca Raton"
                                    error={!!validationErrors.city}
                                    helperText={validationErrors.city}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl
                                    variant="outlined"
                                    style={{ backgroundColor: '#fff' }}
                                    fullWidth
                                    error={!!validationErrors.state}
                                >
                                    <InputLabel>State</InputLabel>
                                    <Select
                                        value={achDetails.state}
                                        onChange={(e) => handleAchDetailChange('state', e.target.value)}
                                        label="State"
                                    >
                                        {usStates.map((state) => (
                                            <MenuItem key={state.value} value={state.value}>
                                                {state.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {validationErrors.state && (
                                        <FormHelperText>{validationErrors.state}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Name on Account"
                                    value={achDetails.nameOnAccount}
                                    onChange={(e) => handleAchDetailChange('nameOnAccount', e.target.value)}
                                    style={{ backgroundColor: '#fff' }}
                                    variant="outlined"
                                    fullWidth
                                    placeholder="John Smith"
                                    error={!!validationErrors.nameOnAccount}
                                    helperText={validationErrors.nameOnAccount}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} className={classes.formRow}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Account Nickname"
                                    value={achDetails.accountNickname}
                                    style={{ backgroundColor: '#fff' }}
                                    onChange={(e) => handleAchDetailChange('accountNickname', e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    placeholder="BofA Checking"
                                />
                            </Grid>

                            <Grid item xs={12} md={6} className={classes.checkboxGroup}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={saveForFutureUse}
                                            onChange={(e) => setSaveForFutureUse(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Save Payment Method For Future Use"
                                />
                            </Grid>
                        </Grid>

                        <Divider style={{ margin: '16px 0' }} />

                        <Box className={classes.buttonGroup}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSaveNewPayment}
                                className={classes.saveButton}
                            >
                                Save
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleCancelNewPayment}
                                className={classes.cancelButton}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Paper>
                )}
            </form>
        </Paper>
    );
};
ACHPaymentForm.propTypes = {
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    handleCancel: PropTypes.func,
    paymentMethods: PropTypes.arrayOf(PropTypes.shape({
        accountName: PropTypes.string.isRequired,
        bankAccountId: PropTypes.number.isRequired,
        isActive: PropTypes.bool.isRequired
    })),
    allowPaymentDateSelection: PropTypes.bool
};
ACHPaymentForm.defaultProps = {
    onSubmit: () => { },
    onCancel: () => { },
    handleCancel: () => { },
    paymentMethods: [],
    allowPaymentDateSelection: false
};
export default ACHPaymentForm;
